import { ChangeDetectionStrategy, Component, input } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { ImageUploadComponent } from "../../../../components/image-upload/image-upload.component";
import { ThemeAssetUploadResponse, ThemeConfigResponse } from "../../models/theme.model";

/**
 * The Display tab's form. Each field pairs an asset key (`^public/.+`, sent to the backend as
 * `themeConfig.*AssetKey`) with its preview URL (read-only, used to re-render in edit mode; never
 * sent). Values are populated by the reusable uploader's `uploaded` event, not by form inputs.
 *
 * The on-page "Tenant Logo" field is backed by the `tenantLogo*` key. The host view renders its header
 * logo from `themeConfig.tenantLogoUrl` (see `host-freeform.component`), and other surfaces (e.g.
 * tenant admin) also consume `tenantLogo*`.
 */
export type DisplayGroup = FormGroup<{
    tenantLogoAssetKey: FormControl<string>;
    tenantLogoUrl: FormControl<string>;
    hostViewWallpaperAssetKey: FormControl<string>;
    hostViewWallpaperUrl: FormControl<string>;
}>;

/** Builds the Display group, optionally pre-filled from a loaded theme config (edit mode). */
export function createDisplayGroup(fb: FormBuilder, config?: ThemeConfigResponse | null): DisplayGroup {
    return fb.nonNullable.group({
        tenantLogoAssetKey: fb.nonNullable.control(config?.tenantLogoAssetKey ?? ""),
        tenantLogoUrl: fb.nonNullable.control(config?.tenantLogoUrl ?? ""),
        hostViewWallpaperAssetKey: fb.nonNullable.control(config?.hostViewWallpaperAssetKey ?? ""),
        hostViewWallpaperUrl: fb.nonNullable.control(config?.hostViewWallpaperUrl ?? "")
    });
}

/**
 * "Office Display" section of the theme form (Display tab). Two independent image fields — the
 * logo (labeled "Tenant Logo" on-page, backed by the tenantLogo key; see DisplayGroup note) and the Host
 * View Wallpaper — each uploaded full-size via the reusable uploader (no crop UI; visual fit is a
 * display-time concern at the render site). The group is parent-owned: this component only
 * reads/mutates the passed-in DisplayGroup.
 */
@Component({
    selector: "app-theme-display",
    templateUrl: "./theme-display.component.html",
    styleUrl: "./theme-display.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ImageUploadComponent]
})
export class ThemeDisplayComponent {
    /** Parent-owned Display group; this component only reads/mutates it. */
    readonly display = input.required<DisplayGroup>();

    /** Stores the uploaded logo asset key + preview URL (labeled "Logo" on-page). */
    onTenantLogoUpload(upload: ThemeAssetUploadResponse): void {
        this.display().controls.tenantLogoAssetKey.setValue(upload.key);
        this.display().controls.tenantLogoUrl.setValue(upload.url);
    }

    /** Stores the uploaded host-view wallpaper asset key + preview URL on the group. */
    onHostViewWallpaperUploaded(upload: ThemeAssetUploadResponse): void {
        this.display().controls.hostViewWallpaperAssetKey.setValue(upload.key);
        this.display().controls.hostViewWallpaperUrl.setValue(upload.url);
    }
}
